import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useRef } from 'react';
import { useObserveTableElementRef } from '../../virtualization/hooks/useObserveTableElementRef';
import { COLUMN } from '../../virtualization/types/observerTypes';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import { DnDWrapper } from '../../dnd/DnDWrapper';
import UIDragHandle from 'UIComponents/draggable/UIDragHandle';
import ResizeAffordance from './ResizeAffordance';
import UIBox from 'UIComponents/layout/UIBox';
import HeaderLeftBorder from './HeaderLeftBorder';
const baseWrapperStyle = {
  // Ensures we fill the whole `th` and line our contents up properly
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};
const draggingWrapperStyle = Object.assign({}, baseWrapperStyle, {
  opacity: 0.5,
  background: GREAT_WHITE
});
export const getContentComponent = ({
  column: {
    columnNamespace,
    columnName,
    resizable,
    reorderable
  },
  originalWidth,
  columnIndex,
  isLastColumn,
  handleResize,
  onResizeEnd,
  onColumnOrderChange
}) => {
  const ContentComponent = DnDWrapper(({
    connectDragSource,
    connectDropTarget,
    connectDragPreview,
    draggedLeft,
    draggedRight,
    isDragging,
    arrowsNode,
    children
  }) => {
    const initialXRef = useRef(0);
    const handleMouseDown = useCallback(initiatingEvent => {
      initialXRef.current = initiatingEvent.clientX; // Prevent the initiating event from sorting or dragging

      initiatingEvent.preventDefault();

      const handleMouseMove = ({
        clientX
      }) => handleResize(originalWidth + (clientX - initialXRef.current));

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', event => {
        // Prevent the terminating event from sorting
        event.preventDefault();
        document.removeEventListener('mousemove', handleMouseMove);
        onResizeEnd();
      }, {
        once: true
      });
    }, []);
    const handleClick = useCallback(event => {
      if (resizable) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, []);
    const handleObserveRef = useObserveTableElementRef();
    const dragHandle = reorderable ? connectDragSource(
    /*#__PURE__*/
    // This must be a div as well
    _jsx("div", {
      children: /*#__PURE__*/_jsx(UIDragHandle, {
        "data-test-id": `drag-header-${columnNamespace}-${columnName}`
      })
    })) : null; // This must be a div. It can't be a styled.div or any non-native element
    // because otherwise react-dnd complains

    const content = /*#__PURE__*/_jsxs("div", {
      style: isDragging ? draggingWrapperStyle : baseWrapperStyle,
      ref: handleObserveRef,
      "data-observer-type": COLUMN,
      "data-observer-id": columnIndex,
      children: [/*#__PURE__*/_jsx(HeaderLeftBorder, {
        shouldHighlight: !isDragging && draggedLeft
      }), /*#__PURE__*/_jsx(UIBox, {
        basis: "5px",
        className: "p-x-2",
        shrink: 0,
        children: dragHandle
      }), children, /*#__PURE__*/_jsx("span", {
        className: "p-right-2 m-left-auto",
        children: arrowsNode
      }), resizable && /*#__PURE__*/_jsx(ResizeAffordance, {
        "data-test-id": `resize-header-${columnNamespace}-${columnName}`,
        shouldHighlight: !isDragging && draggedRight,
        shouldOffsetRight: !isLastColumn,
        onMouseDown: handleMouseDown,
        onClick: handleClick
      })]
    });

    if (reorderable) {
      return connectDropTarget(connectDragPreview(content));
    }

    return content;
  });
  return props => /*#__PURE__*/_jsx(ContentComponent, Object.assign({}, props, {
    // These props are required for the DnD wrapper to function
    columnNamespace: columnNamespace,
    columnName: columnName,
    columnIndex: columnIndex,
    onColumnOrderChange: onColumnOrderChange
  }));
};